import { useTranslation } from 'react-i18next';
import { Facebook, Instagram, Mail } from 'lucide-react';

/**
 * Footer Component
 * 
 * Site footer with brand information, legal links, and social media links.
 * Features:
 * - Brand name with gradient styling
 * - Legal page links (Privacy, Terms, Cookies, Data Deletion)
 * - Social media links (Facebook, Instagram)
 * - Contact email
 * - Copyright notice with current year
 * - Dark gradient background with colored top border accent
 * 
 * The footer provides important legal information and additional ways for users
 * to connect with the company through social media.
 * 
 * @component
 * @example
 * ```tsx
 * <Footer />
 * ```
 */
export default function Footer() {
  const { t } = useTranslation('common');
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { name: 'Facebook', icon: Facebook, url: 'https://www.facebook.com/suersdey' },
    { name: 'Instagram', icon: Instagram, url: 'https://www.instagram.com/suersdey/' },
  ];

  const legalLinks = [
    { label: t('footer.legal.privacy'), href: '/privacy-policy' },
    { label: t('footer.legal.terms'), href: '/terms-of-service' },
    { label: t('footer.legal.cookies'), href: '/cookie-policy' },
    { label: t('footer.legal.dataDeletion'), href: '/data-deletion' },
  ];

  return (
    <footer className="bg-gradient-to-b from-neutral-900 to-neutral-950 text-white border-t-4 border-primary-500">
      <div className="container-custom py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Brand */}
          <div>
            <h3 className="text-2xl font-display font-bold bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent mb-4">
              Suersdey
            </h3>
            <p className="text-neutral-300 text-sm">
              {t('footer.tagline')}
            </p>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-semibold mb-4 text-neutral-200">Legal</h4>
            <ul className="space-y-2">
              {legalLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-neutral-400 hover:text-primary-400 text-sm transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Social */}
          <div>
            <h4 className="font-semibold mb-4 text-neutral-200">Connect</h4>
            <div className="flex space-x-4 mb-4">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-neutral-400 hover:text-primary-400 transition-colors"
                    aria-label={social.name}
                  >
                    <Icon size={24} />
                  </a>
                );
              })}
            </div>
            <a
              href="mailto:hello@suersdey.com"
              className="text-neutral-400 hover:text-primary-400 text-sm flex items-center gap-2 transition-colors"
            >
              <Mail size={16} />
              hello@suersdey.com
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-neutral-800 pt-8 text-center">
          <p className="text-neutral-400 text-sm mb-2">
            {t('footer.copyright', { year: currentYear })}
          </p>
          <p className="text-neutral-500 text-sm">
            {t('footer.madeIn')}
          </p>
        </div>
      </div>
    </footer>
  );
}
